import { useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { DealAlertCaseStudy } from "@/components/sections/DealAlertCaseStudy";
import { HausDesHandysCaseStudy } from "@/components/sections/HausDesHandysCaseStudy";
import { NAPerformanceCaseStudy } from "@/components/sections/NAPerformanceCaseStudy";
import { NAAutomobileCaseStudy } from "@/components/sections/NAAutomobileCaseStudy";
import { NovaPilotCaseStudy } from "@/components/sections/NovaPilotCaseStudy";
import { UnfallHeldCaseStudy } from "@/components/sections/UnfallHeldCaseStudy";
import { PendingCaseStudy } from "@/components/sections/CaseStudyShell";
import { useLang } from "@/i18n/translations";
import { useSEO } from "@/hooks/useSEO";
import { useSmoothScroll, ScrollTrigger } from "@/hooks/useSmoothScroll";
import { PROJECTS } from "@/lib/siteConfig";

// Full case-study components per project (extended as assets arrive)
const FULL_STUDIES = {
  dealalert: DealAlertCaseStudy,
  hausdeshandys: HausDesHandysCaseStudy,
  naperformance: NAPerformanceCaseStudy,
  naautomobile: NAAutomobileCaseStudy,
  novapilot: NovaPilotCaseStudy,
  unfallheld: UnfallHeldCaseStudy,
};

// Per-page SEO (German — primary site language)
const SEO_META = {
  dealalert: {
    title: "DealAlert Case Study | CloudService.hr",
    description:
      "AI Asset Intelligence für Auto und Immobilien: Datenpipeline, konservative Markt-Engine, iOS App, AI Advisor und Referral-System — von CloudService gebaut.",
  },
  hausdeshandys: {
    title: "HausDesHandys Case Study | CloudService.hr",
    description:
      "Digitalisierung eines Smartphone-Reparaturbetriebs mit Repair-Konfigurator, AI Assistant, Smart Pricing und Admin Dashboard.",
  },
  naperformance: {
    title: "NA Performance Case Study | CloudService.hr",
    description:
      "AI Performance Advisor für Fahrzeugoptimierung: Zulassungsschein-Upload, KI-Analyse, Leistungsvergleich, Stage-Empfehlungen und WhatsApp Lead Flow.",
  },
  novapilot: {
    title: "NovaPilot Case Study | CloudService.hr",
    description:
      "Digitaler Autoimport-Assistent mit NoVA-Rule-Engine: geführter Fall-Wizard, OCR-Dokumente, USt- und Preislogik, Risikoampel und nachvollziehbares PDF-Berechnungsprotokoll.",
  },
  unfallheld: {
    title: "UnfallHeld Case Study | CloudService.hr",
    description:
      "Digitaler Unfallassistent mit KI-Schadenaufnahme: geführte Unfallmeldung, Foto-Upload, Voice-to-Text, Zulassungsschein-OCR, unverbindliche KI-Ersteinschätzung, PDF-Schadensbericht und Betreiber-Dashboard.",
  },
  naautomobile: {
    title: "NA Automobile Case Study | CloudService.hr",
    description:
      "Premium Autohaus-Plattform: Fahrzeugbestand, WhatsApp-first Verkauf, unverbindlicher Finanzierungsrechner, KI-gestützte Ankaufseinschätzung, Händler-Dashboard und AI Photo Studio mit View-Lock.",
  },
};

export default function CaseStudyPage() {
  const { projectId } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const { scrollTo } = useSmoothScroll();
  const project = PROJECTS.find((p) => p.id === projectId);

  const meta = SEO_META[projectId] || {};
  useSEO(meta.title, meta.description);

  useEffect(() => {
    // Instant scroll reset (Lenis-aware) when entering / switching case studies
    scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    const id = setTimeout(() => ScrollTrigger.refresh(), 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (!project) return <Navigate to="/" replace />;

  const Full = FULL_STUDIES[projectId];

  // Note: Home re-runs ScrollTrigger.refresh() ~600ms after mount (pinned intro).
  // Scrolling must start AFTER that refresh, otherwise the Lenis tween gets cancelled.
  const backToWall = () => {
    navigate("/");
    setTimeout(() => scrollTo("#referenzen"), 800);
  };
  const goContact = () => {
    navigate("/");
    setTimeout(() => scrollTo("#kontakt"), 800);
  };

  return (
    <div className="relative bg-[#05070d] min-h-screen">
      <Navbar />
      <main className="pt-16">
        {/* Back bar */}
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 pt-6">
          <button
            data-testid="case-back-link"
            onClick={backToWall}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#00d4ff] transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" /> {t.caseNav.back}
          </button>
        </div>

        {Full ? <Full /> : <PendingCaseStudy project={project} />}

        {/* CTA */}
        <section className="py-20 border-t border-white/5 bg-[#070a12]" data-testid="case-cta">
          <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">
              {t.caseNav.ctaTitle}
            </h2>
            <p className="mt-3 text-base text-muted-foreground max-w-xl mx-auto">{t.caseNav.ctaText}</p>
            <Button
              data-testid="case-cta-button"
              onClick={goContact}
              className="mt-7 bg-[#00d4ff] text-[#05070d] hover:bg-[#33ddff] font-medium rounded-full px-7 h-11 shadow-[0_0_24px_rgba(0,212,255,0.18)] transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-px"
            >
              {t.nav.startProject}
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
